import React, { memo, useCallback, useMemo } from 'react';

import { Box, Tooltip, Typography } from '@mui/material';
import AccordionCDS from '@sharedCR/coreDesignSystem/AccordionCDS';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

import { SignatureRequestStatus } from 'constants/loanApplication';

import { GENERATED_OUTSIDE_TOOLTIP_SLOT_PROPS } from '../../DisclosureDelivery.constants';
import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';
import {
  getGeneratedOutsidePackageTooltip,
  getPackageStatusLabel,
  getPackageSubtitle,
} from '../../DisclosureDelivery.utils';
import DisclosureAuditStrip from '../DisclosureAuditStrip';
import DisclosureSignerTable from '../DisclosureSignerTable';
import FormDeliveryReadinessChip from '../FormDeliveryReadinessChip';
import ScheduledDeliveryTable from '../ScheduledDeliveryTable';

import { accordionCustomStyles } from './DisclosurePackageItem.styles';

const DisclosurePackageItem = ({ item, onResendPackage, canResend = false }) => {
  // Not derived from item.status: a package cancelled before its scheduled generation reports
  // Canceled, and its body must still show what was scheduled rather than an empty signer table.
  const isScheduled = !!item.isScheduledDelivery;
  // Drives both the withheld resend — which would issue the recipient a fresh live signing link for
  // a package the loan team cancelled — and the suppressed generated-outside tooltip, whose copy
  // describes the package as awaiting signature. Neither hides the underlying send loan action.
  const isCanceled = item.status === SignatureRequestStatus.CANCELED;

  const handleResendPackage = useCallback(() => onResendPackage?.(item), [onResendPackage, item]);

  const additionalSummaryContent = useMemo(() => {
    const packageStatus = getPackageStatusLabel(item);
    const statusLabel = (
      <LabelCDS size={LabelSize.S} title={packageStatus.title} variant={packageStatus.variant} />
    );

    return (
      <>
        <Box className="disclosure-package-subtitle">
          <Typography color="text.secondary" variant="regular12">
            {getPackageSubtitle(item, { isScheduled })}
          </Typography>
          <FormDeliveryReadinessChip status={item.readinessStatus} />
        </Box>
        <Box
          className="disclosure-package-status"
          data-testid={DisclosureDeliveryTestIds.PACKAGE_STATUS_LABEL}
        >
          {item.isGeneratedOutside && !isCanceled ? (
            <Tooltip
              arrow
              slotProps={GENERATED_OUTSIDE_TOOLTIP_SLOT_PROPS}
              title={getGeneratedOutsidePackageTooltip(item.status)}
            >
              <Box className="flex">{statusLabel}</Box>
            </Tooltip>
          ) : (
            statusLabel
          )}
        </Box>
      </>
    );
  }, [item, isScheduled, isCanceled]);

  return (
    <AccordionCDS
      additionalSummaryContent={additionalSummaryContent}
      arrowSize={18}
      className="py-2"
      customStyles={accordionCustomStyles}
      data-testid={DisclosureDeliveryTestIds.getPackageHeaderId(item.formBundleSignatureRequestId)}
      title={item.formBundleName}
    >
      {isScheduled ? (
        <ScheduledDeliveryTable
          formBundleId={item.formBundleId}
          generatedDate={item.scheduledDeliveryDate}
          isCanceled={isCanceled}
        />
      ) : (
        <>
          <DisclosureSignerTable
            applicationId={item.applicationId}
            createdDate={item.createdDate}
            formBundleName={item.formBundleName}
            formBundleSignatureRequestId={item.formBundleSignatureRequestId}
            generatedDate={item.createdDate}
            isGeneratedOutside={item.isGeneratedOutside}
            isSignable={item.isSignable}
            signatureItems={item.signatureItems}
            onResendPackage={canResend && !isCanceled ? handleResendPackage : undefined}
          />
          <DisclosureAuditStrip />
        </>
      )}
    </AccordionCDS>
  );
};

DisclosurePackageItem.propTypes = {
  item: PropTypes.shape({
    applicationId: PropTypes.string,
    documentId: PropTypes.string,
    formBundleId: PropTypes.string,
    formBundleSignatureRequestId: PropTypes.string,
    formBundleName: PropTypes.string,
    status: PropTypes.string,
    isSignable: PropTypes.bool,
    isScheduledDelivery: PropTypes.bool,
    isGeneratedOutside: PropTypes.bool,
    acknowledgedDate: PropTypes.string,
    createdDate: PropTypes.string,
    readinessStatus: PropTypes.string,
    sentDate: PropTypes.string,
    scheduledDeliveryDate: PropTypes.string,
    signatureItems: PropTypes.array,
  }).isRequired,
  canResend: PropTypes.bool,
  onResendPackage: PropTypes.func,
};

export default memo(DisclosurePackageItem);
